import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
} from 'vue-router';
import { getController } from '@/api/controllers';
import { useChunkError } from '@/composables/useChunkError';
import { useAuthStore } from '@/stores/auth';
import { useBannerStore } from '@/stores/banner';
import { useControllersStore } from '@/stores/controllers';
import { useGroupsStore } from '@/stores/groups';
import { useOrganizationsStore } from '@/stores/organizations';
import { useResetStore } from '@/utils/useResetStore';
import VReloadView from '@/views/VReloadView/VReloadView.vue';
import { changeEmail } from './changeEmail';
import { checkControllerOrganization } from './checkControllerOrganization';
import { checkIsOrgExists } from './checkIsOrgExists';
import { deauthorize } from './deauthorize';
import { forbidToAuthorized } from './forbidToAuthorized';
import { getLoginWithRedirect } from './getLoginWithRedirect';
import { invite } from './invite';
import { preloadUserInfo } from './preloadUserInfo';
import { redirectFromRoot } from './redirectFromRoot';
import { uppercaseSerialNumber } from './uppercaseSerialNumber';
import { verifyUser } from './verifyUser';

const VAddControllerView = () =>
  import('@/views/VAddControllerView/VAddControllerView.vue');
const VControllerView = () =>
  import('@/views/VControllerView/VControllerView.vue');
const VControllersView = () =>
  import('@/views/VControllersView/VControllersView.vue');
const VLoginView = () => import('@/views/VLoginView/VLoginView.vue');
const VSignUpView = () => import('@/views/VSignUpView/VSignUpView.vue');
const VConnectView = () => import('@/views/VConnectView/VConnectView.vue');
const VResetPasswordView = () =>
  import('@/views/VResetPasswordView/VResetPasswordView.vue');
const VResetPasswordRequestView = () =>
  import('@/views/VResetPasswordRequestView/VResetPasswordRequestView.vue');
const VOrganizationsView = () =>
  import('@/views/VOrganizationsView/VOrganizationsView.vue');
const VOrganizationView = () =>
  import('@/views/VOrganizationView/VOrganizationView.vue');
const VLoadingView = () => import('@/views/VLoadingView/VLoadingView.vue');
const VSecurityView = () => import('@/views/VSecurityView/VSecurityView.vue');
const VTransferControllersView = () => import('@/views/VTransferControllersView/VTransferControllersView.vue');
const VUnsubscribeView = () =>
  import('@/views/VUnsubscribeView/VUnsubscribeView.vue');

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'root',
      component: VLoadingView,
      beforeEnter: [redirectFromRoot],
    },
    {
      path: '/controllers',
      name: 'add-controller',
      component: VAddControllerView,
      meta: { requiresAuth: true },
      beforeEnter: [preloadUserInfo],
    },
    {
      path: '/organizations',
      name: 'organizations',
      component: VOrganizationsView,
      meta: { requiresAuth: true },
      beforeEnter: [preloadUserInfo],
    },
    {
      path: '/organizations/:organizationId',
      name: 'organization',
      component: VOrganizationView,
      meta: { requiresAuth: true },
      beforeEnter: [preloadUserInfo, checkIsOrgExists],
    },
    {
      path: '/security',
      name: 'security',
      component: VSecurityView,
      meta: { requiresAuth: true },
      beforeEnter: [preloadUserInfo],
    },
    {
      path: '/organizations/:organizationId/controllers',
      name: 'controllers',
      component: VControllersView,
      meta: { requiresAuth: true },
      beforeEnter: [preloadUserInfo, checkIsOrgExists],
    },
    {
      // This route exists to support links that were created before we added multi-organization support
      path: '/controllers/:serialNumber',
      component: VLoadingView,
      meta: { requiresAuth: true },
      beforeEnter: [
        uppercaseSerialNumber,
        async (to) => {
          try {
            const controller = await getController(
              String(to.params.serialNumber),
            );

            return {
              name: 'controller',
              params: {
                ...to.params,
                organizationId: controller.organization.id,
              },
            };
          } catch {
            return {
              name: 'organizations',
            };
          }
        },
      ],
    },
    {
      path: '/organizations/:organizationId/controllers/:serialNumber',
      name: 'controller',
      component: VControllerView,
      meta: { requiresAuth: true },
      beforeEnter: [preloadUserInfo, checkIsOrgExists, uppercaseSerialNumber, checkControllerOrganization],
    },
    {
      path: '/login',
      name: 'login',
      component: VLoginView,
      meta: { requiresAuth: false },
      beforeEnter: [forbidToAuthorized],
    },
    {
      path: '/connect/:protocol([a-z0-9]+)/:serialNumber/:target(.*)*',
      name: 'connect',
      component: VConnectView,
      meta: { requiresAuth: true },
      beforeEnter: [uppercaseSerialNumber],
    },
    {
      path: '/signup',
      name: 'signup',
      component: VSignUpView,
      meta: { requiresAuth: false },
      beforeEnter: [deauthorize],
    },
    {
      path: '/invite',
      name: 'invite',
      component: VLoadingView,
      meta: { requiresAuth: false }, // Redirects are handled in invite.ts
      beforeEnter: [invite],
    },
    {
      path: '/users/emails',
      name: 'email-change',
      component: VLoadingView,
      meta: { requiresAuth: true },
      beforeEnter: [changeEmail],
    },
    {
      path: '/controllers-transfer',
      name: 'controllers-transfer',
      component: VTransferControllersView,
      meta: { requiresAuth: true },
      beforeEnter: [preloadUserInfo],
    },
    {
      path: '/users/registrations',
      name: 'user-verify',
      component: VLoadingView,
      meta: { requiresAuth: false },
      beforeEnter: [verifyUser],
    },
    {
      path: '/unsubscribe',
      name: 'unsubscribe',
      component: VUnsubscribeView,
      meta: { requiresAuth: false },
    },
    {
      path: '/password-reset/',
      name: 'password-reset-request',
      component: VResetPasswordRequestView,
    },
    {
      path: '/password-reset/:uid/:token/',
      name: 'password-reset',
      component: VResetPasswordView,
    },
    {
      path: '/password-change',
      name: 'password-change',
      redirect: '/security',
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
    {
      path: '/update-required',
      name: 'update-required',
      component: VReloadView,
      beforeEnter: [preloadUserInfo],
    },
  ],
});

router.beforeEach((to: RouteLocationNormalized) => {
  const authStore = useAuthStore();
  const bannerStore = useBannerStore();
  const organizationsStore = useOrganizationsStore();
  const groupsStore = useGroupsStore();
  const controllersStore = useControllersStore();

  if (bannerStore.closable) {
    bannerStore.$reset();
  }

  // Reset organizations store when switching between organizations
  if (
    to.params.organizationId &&
    organizationsStore.organization?.id &&
    to.params.organizationId !== organizationsStore.organization?.id
  ) {
    controllersStore.$reset();
    organizationsStore.$reset();
    groupsStore.$reset();
  }

  if (to.meta.requiresAuth && !authStore.isAuthorized.value) {
    return getLoginWithRedirect(to);
  }
});

router.afterEach(() => {
  const authStore = useAuthStore();

  if (!authStore.isAuthorized.value) {
    const resetStore = useResetStore();
    resetStore.all();
  }
});

router.onError((err, to) => {
  const isError = useChunkError();
  const isChunkError =
    err?.message?.includes('Failed to fetch dynamically imported module') ||
    err?.message?.includes('Loading chunk') ||
    err?.name === 'ChunkLoadError';

  if (isChunkError) {
    isError.value = true;
    router.replace({
      path: '/update-required',
      query: { redirect: to.fullPath },
    });
  }
});

export default router;
